import {
  GET_TICKET_TEMPLATE,
  GET_TICKET_TEMPLATES_SUMMARY,
} from "../TicketingAPI.js";
import { useApolloClient, useQuery } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { Loader, useIcon } from "@SeemplicityDev/shared";
import { DateTime } from "luxon";
import TicketTemplatePreview from "./TicketTemplatePreview/TicketTemplatePreview.js";
import type { TemplateField } from "./TicketTemplatePreview/TicketTemplatePreview";
import {
  fieldTypeOptions,
  textEditorFields,
} from "../GenericField/GenericField.js";
import type { GenericFieldProps } from "../GenericField/GenericField";
import type { UseFormReturn } from "react-hook-form";

export const TemplateModeEnum = {
  APPLY: "APPLY",
  COPY: "COPY",
} as const;

export type TemplateMode =
  (typeof TemplateModeEnum)[keyof typeof TemplateModeEnum];

/** Raw template field as returned by the GET_TICKET_TEMPLATE query. */
interface RawTemplateField {
  display_name?: string;
  field_name?: string;
  display_value?: string;
  value?: string;
  is_compatible?: boolean;
}

/** A single ticket template summary node. */
export interface TemplateSummary {
  id?: string;
  name?: string;
  description?: string;
  fields?: TemplateField[];
  withHelp?: string;
  // The server payload carries additional fields; keep it open for the
  // properties the runtime relies on without over-constraining.
  [key: string]: unknown;
}

/** A fully-loaded template (with formatted fields) shown in the preview. */
export interface DisplayedTemplate {
  id?: string;
  created_by?: string;
  created_time?: string;
  description?: string;
  is_compatible?: boolean;
  fields?: TemplateField[];
  [key: string]: unknown;
}

/** Option object captured for SELECT_QUERY fields. */
interface SelectedOptionObject {
  value?: unknown;
  label?: string;
  [key: string]: unknown;
}

/** Shape returned by `getFieldsMutationArray` entries (mirrors the consumer). */
interface MutationField {
  key: string;
  value: unknown;
}

/** A custom field entry stored inside the `custom_fields` form value. */
interface CustomFieldTemplateObj {
  field: string;
  value: unknown;
  templateDisabled: boolean;
  displayValue?: string;
}

export interface UseTicketTemplateReturn {
  templates: TemplateSummary[] | undefined;
  displayedTemplate: DisplayedTemplate | undefined;
  onSelectTemplateToDisplay: (templateId: string | number) => void;
  showTemplate: () => React.ReactElement;
  getFieldsTemplatePreview: () => TemplateField[];
  onSelectedOptionObjectChange: (
    fieldName: string,
    selectedOptionObject: SelectedOptionObject
  ) => void;
  applyTemplateValues: (
    templateToApply: DisplayedTemplate | undefined,
    mode?: TemplateMode
  ) => void;
  onNewTemplateCreated: (template?: TemplateSummary) => void;
  formatTemplateFields: (
    fields: RawTemplateField[] | undefined
  ) => TemplateField[] | undefined;
}

const useTicketTemplate = (
  ticketProviderId: string | undefined,
  keysObject: unknown,
  formMethods: UseFormReturn,
  fetchPolicy: string | undefined,
  onData: () => void,
  onCreateTicketTemplateSuccess: (name?: string) => void,
  getFieldsMutationArray: (
    formValues: Record<string, unknown>
  ) => MutationField[],
  getFieldPropsByFieldName: (fieldName: string) => GenericFieldProps | undefined,
  showTicketTemplates = true
): UseTicketTemplateReturn => {
  const client = useApolloClient();
  const { getSvg } = useIcon();
  const [displayedTemplate, setDisplayedTemplate] = useState<
    DisplayedTemplate | undefined
  >();
  const [templates, setTemplates] = useState<TemplateSummary[] | undefined>();
  const [searchQueryFieldsDisplayValues, setSearchQueryFieldsDisplayValues] =
    useState<Record<string, SelectedOptionObject>>({});

  const { getValues, reset: formReset, setValue } = formMethods;

  const { data: templatesData } = useQuery(GET_TICKET_TEMPLATES_SUMMARY, {
    // `fetchPolicy` is a plain string prop; cast to the Apollo union (matches
    // the TicketEndpointFields precedent for this pass-through value).
    fetchPolicy: fetchPolicy as never,
    variables: {
      ticket_provider_id: ticketProviderId,
      has_ticket_provider: true,
    },
    skip: !ticketProviderId || !showTicketTemplates,
  });

  useEffect(() => {
    if (templatesData?.ticket_templates_summary?.edges?.length > 0) {
      setTemplates(
        templatesData?.ticket_templates_summary?.edges?.map(
          (opt: { node: TemplateSummary }) => ({
            ...opt.node,
          })
        )
      );
    }
  }, [templatesData]);

  const onSelectedOptionObjectChange = (
    fieldName: string,
    selectedOptionObject: SelectedOptionObject
  ) => {
    setSearchQueryFieldsDisplayValues({
      ...searchQueryFieldsDisplayValues,
      [fieldName]: selectedOptionObject,
    });
  };

  const formatTemplateFields = (
    fields: RawTemplateField[] | undefined
  ): TemplateField[] | undefined => {
    return fields?.map((field) => {
      let displayVal: unknown = field.display_value;
      let val: unknown = field.value;

      try {
        displayVal = JSON.parse(field.display_value as string);
        val = JSON.parse(field.value as string);
      } catch {}

      return {
        label: field.display_name,
        displayValue: Array.isArray(displayVal)
          ? displayVal?.join(", ")
          : (displayVal as string | undefined),
        value: val,
        isCompatible: field.is_compatible,
        key: field.field_name as string,
      };
    });
  };

  const showTemplate = (): React.ReactElement => {
    return !displayedTemplate ? (
      <Loader flexcenter fullHeight img={getSvg("icon-spinner")} />
    ) : (
      <div className={"template-select-display-area"}>
        <div className={"template-created-by"}>
          Created By {displayedTemplate.created_by},{" "}
          {
            //TODO: move DateUtils.js into package and use getFormattedDate
            // `DateTime`'s constructor is private in luxon's types; preserve the
            // exact runtime call by casting the constructor (behavior unchanged).
            new (DateTime as unknown as new (
              date?: string
            ) => DateTime)(
              displayedTemplate?.created_time
            ).toLocaleString(DateTime.DATE_MED)
          }
        </div>
        {displayedTemplate.description && (
          <div className={"template-description"}>
            {displayedTemplate.description}
          </div>
        )}
        <TicketTemplatePreview
          fields={displayedTemplate.fields}
          isDeletable={false}
        />
      </div>
    );
  };

  const onSelectTemplateToDisplay = (templateId: string | number) => {
    if (displayedTemplate?.id !== templateId) {
      setDisplayedTemplate(undefined);

      client
        .query({
          query: GET_TICKET_TEMPLATE,
          variables: {
            ticket_provider_id: ticketProviderId,
            template_id: templateId,
            selected_keys: JSON.stringify(keysObject),
          },
        })
        .then((res) => {
          const formattedTemplateFields = formatTemplateFields(
            res?.data?.ticket_template?.fields
          );

          setDisplayedTemplate({
            ...res?.data?.ticket_template,
            fields: formattedTemplateFields,
          });

          const enrichedOptions = templates?.map((t) =>
            t.id === templateId
              ? {
                  ...t,
                  fields: formattedTemplateFields,
                  withHelp: !res?.data?.ticket_template?.is_compatible
                    ? "Some saved fields don't apply to the selected issue type"
                    : "",
                }
              : t
          );

          setTemplates(enrichedOptions);
        });
    }
  };

  const getFieldDisplayValue = (
    fieldValue: unknown,
    fieldProps: GenericFieldProps | undefined
  ): unknown => {
    switch (fieldProps?.input) {
      case fieldTypeOptions.TAGS_LIST:
        return (fieldValue as unknown[]).reduce<
          { id?: string; name?: string }[]
        >((options, singleVal) => {
          const optionValue = fieldProps?.options?.find(
            (opt) => opt.id === singleVal
          );

          if (!!optionValue) {
            return options.concat(optionValue);
          } else if (fieldProps?.creatable) {
            return options.concat({
              id: singleVal as string,
              name: singleVal as string,
            });
          }

          return options;
        }, []);
      case fieldTypeOptions.MULTISELECT:
        return (fieldValue as string[] | undefined)?.join(", ");
      case fieldTypeOptions.SELECT:
        // for each type that has value that is selected from list we'll save both value key
        // and display name (this is the only types that is in use in TicketEndpointFields -
        // multiSelect, radio and colorSelect are not in used and therefor don't
        // have special treatment currently)
        return fieldProps?.options?.find((opt) => opt.id === fieldValue)?.name;
      case fieldTypeOptions.SELECT_QUERY:
        if (
          searchQueryFieldsDisplayValues[fieldProps?.name as string]?.value ===
          fieldValue
        ) {
          return searchQueryFieldsDisplayValues[fieldProps?.name as string]
            ?.label;
        }
      default:
        return fieldValue;
    }
  };

  const getFieldsTemplatePreview = (): TemplateField[] => {
    const formValues = getValues("fields");
    return getFieldsMutationArray(formValues)
      .filter(
        (field) =>
          (!Array.isArray(field.value) && !!field.value) ||
          (field.value as unknown[]).length > 0
      )
      .map((field) => {
        const fieldProps = getFieldPropsByFieldName(field.key);

        return {
          ...field,
          displayValue: getFieldDisplayValue(field.value, fieldProps) as
            | string
            | undefined,
          label: fieldProps?.label as string | undefined,
          fieldType: fieldProps?.input as TemplateField["fieldType"],
        };
      });
  };

  // Convert template value to the proper format for text editor fields
  const convertValueForField = (
    value: unknown,
    fieldProps: GenericFieldProps | undefined
  ): unknown => {
    if (!fieldProps || !textEditorFields.includes(fieldProps?.input as never)) {
      return value;
    }

    // If value is already in the correct format, return it
    if (typeof value === "object" && value !== null) {
      const objValue = value as { html?: unknown; text?: unknown };
      if (objValue.html !== undefined || objValue.text !== undefined) {
        return value;
      }
    }

    // For HTML_TEXT_AREA, wrap in { html: value }
    // For INLINE_AUTOCOMPLETE and AUTOCOMPLETE_ONLY_TEXTAREA, wrap in { text: value }
    if (fieldProps.input === fieldTypeOptions.HTML_TEXT_AREA) {
      return { html: value };
    } else {
      return { text: value };
    }
  };

  const applyTemplateValues = (
    templateToApply: DisplayedTemplate | undefined,
    mode: TemplateMode = TemplateModeEnum.APPLY
  ) => {
    let fieldsCurrVals = getValues("fields");

    templateToApply?.fields?.forEach((templateField) => {
      if (templateField.isCompatible) {
        if (Object.keys(fieldsCurrVals)?.includes(templateField.key)) {
          const fieldProps = getFieldPropsByFieldName(templateField.key);
          fieldsCurrVals[templateField.key] = convertValueForField(
            templateField.value,
            fieldProps
          );
          // Field not found at top level - check if it exists as an option in custom_fields before treating as new custom field
        } else if (
          getFieldPropsByFieldName("custom_fields")
            ?.mapping?.rows?.map(
              (row) => (row?.field_from as { name?: string })?.name
            )
            ?.includes(templateField.key)
        ) {
          // Find the field props from the custom_fields mapping
          const customFieldProps = getFieldPropsByFieldName("custom_fields")
            ?.mapping?.rows?.find(
              (row) =>
                (row?.field_from as { name?: string })?.name ===
                templateField.key
            )?.field_from as GenericFieldProps | undefined;

          const customFieldTemplateObj: CustomFieldTemplateObj = {
            field: templateField.key,
            value: convertValueForField(templateField.value, customFieldProps),
            templateDisabled: mode === TemplateModeEnum.APPLY,
            displayValue: templateField.displayValue,
          };

          const index = fieldsCurrVals.custom_fields?.findIndex(
            (customField: CustomFieldTemplateObj) =>
              customField.field === templateField.key
          );

          if (index === -1) {
            fieldsCurrVals.custom_fields?.push(customFieldTemplateObj);
          } else {
            fieldsCurrVals.custom_fields[index] = customFieldTemplateObj;
          }
        }
      }
    });

    formReset({
      ...getValues(),
      fields: fieldsCurrVals,
    });
    onData();
  };

  const onNewTemplateCreated = (template?: TemplateSummary) => {
    onCreateTicketTemplateSuccess(template?.name);
    setValue("template", template);
    client.refetchQueries({
      include: ["getTicketTemplatesSummary"],
    });
  };

  return {
    templates,
    displayedTemplate,
    onSelectTemplateToDisplay,
    showTemplate,
    getFieldsTemplatePreview,
    onSelectedOptionObjectChange,
    applyTemplateValues,
    onNewTemplateCreated,
    formatTemplateFields,
  };
};

export default useTicketTemplate;
